import React, { useState } from 'react';
import { ceebEditIssuedApi } from '../services/api';
import type { CeebPushStatusResponse } from '../services/api';
import { Icon } from '../ds/components/Icon';
import { saveBlob } from '../utils/download';
import { alarmChangeText } from '../utils/ceebEditIssued';

/**
 * Kolumna „Status CEEB" w trybie poprawki wystawionego protokołu (`mode: 'edit-issued'`, 2026-10-09).
 * Ten sam status-file co „Wyślij do CEEB", ale inne komunikaty: próba bez zapisu musi jasno mówić,
 * że w CEEB nic się nie zmieniło, a alarm (zmiana poza zakresem bota / spadek do wersji roboczej) ma być
 * widoczny od razu. Pod wynikiem: kopie protokołu przed/po (PDF) i raport przebiegu.
 */
type Props = {
  buildingId: string;
  push: Pick<CeebPushStatusResponse,
    'dryRun' | 'step' | 'code' | 'alarm' | 'editIssued' | 'reportUrl' | 'snapshotBeforeId' | 'snapshotAfterId'
    | 'synced' | 'apartmentCount'> & {
    status: 'running' | 'done' | 'error';
    error?: string;
  };
};

const caption = (color: string, bold = false): React.CSSProperties => ({
  fontSize: 'var(--text-caption)',
  color,
  fontWeight: bold ? 'var(--weight-semibold)' : undefined,
});

const linkButton: React.CSSProperties = {
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',
  fontSize: 'var(--text-caption)',
  color: 'var(--action-primary)',
  textDecoration: 'underline',
};

/** Ile nieoczekiwanych zmian alarmu wypisać w kolumnie — reszta jest w raporcie przebiegu. */
const ALARM_LINES = 3;

/**
 * Pobranie PDF-u kopii protokołu (authedFetch — goły link nie przeszedłby AUTH_ENFORCE).
 * Błąd (np. 404 „kopia bez PDF-u") jest widoczny pod przyciskiem, nie tylko w konsoli.
 */
export function SnapshotPdfButton({ buildingId, snapshotId, label, fallbackName }: {
  buildingId: string;
  snapshotId: string;
  label: string;
  fallbackName: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const { blob, filename } = await ceebEditIssuedApi.snapshotPdf(buildingId, snapshotId);
      saveBlob(blob, filename || fallbackName);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się pobrać kopii protokołu');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" disabled={busy} onClick={() => { void download(); }} style={linkButton}>
        {busy ? 'Pobieranie…' : label}
      </button>
      {error && <span role="alert" style={caption('var(--status-deny)')}>{error}</span>}
    </>
  );
}

function headline(push: Props['push'], hasAlarm: boolean): { text: string; color: string } {
  if (push.status === 'running') {
    return { text: push.dryRun ? 'Poprawianie… (próba bez zapisu)' : 'Poprawianie…', color: 'var(--action-primary)' };
  }
  if (push.status === 'done') {
    if (push.dryRun) return { text: 'Próba bez zapisu zakończona — nic nie zmieniono w CEEB', color: 'var(--changed-text)' };
    if (hasAlarm) return { text: 'Poprawka zakończona z ALARMEM', color: 'var(--status-deny)' };
    // Przebieg bez żadnej zmiany w CEEB (wszystkie lokale wstrzymane) to nie sukces.
    if (push.code === 'EDIT_ISSUED_NOOP') return { text: 'Nic nie poprawiono — protokół w CEEB bez zmian', color: 'var(--status-rec)' };
    return { text: 'Poprawiono protokół w CEEB', color: 'var(--status-ok)' };
  }
  if (push.code === 'AFTER_SNAPSHOT_FAILED') return { text: 'Poprawka niepotwierdzona', color: 'var(--status-deny)' };
  return { text: push.dryRun ? 'Próba bez zapisu przerwana' : 'Poprawka przerwana', color: 'var(--status-deny)' };
}

export function CeebEditIssuedStatus({ buildingId, push }: Props) {
  const alarm = push.alarm || null;
  const wynik = push.editIssued;
  const diff = wynik?.diff || null;
  const finished = push.status !== 'running';
  const head = headline(push, !!alarm);
  const sent = wynik?.sentLokale || [];
  const zmiany = alarm?.unexpected || [];
  const nazwa = (faza: string) => `kopia_protokolu_${faza}.pdf`;
  return (
    <>
      <span style={caption(head.color, push.status !== 'running')}>{head.text}</span>
      {push.status === 'running' && push.step && <span style={caption('var(--text-secondary)')}>{push.step}</span>}
      {push.status === 'error' && push.error && push.error !== alarm?.message && (
        <span style={caption('var(--status-deny)')}>{push.error}</span>
      )}
      {push.status === 'done' && !push.dryRun && sent.length > 0 && (
        <span style={caption('var(--text-secondary)')}>Lokale: {sent.join(', ')}</span>
      )}
      {push.status === 'done' && !push.dryRun && push.synced != null && push.apartmentCount != null && (
        <span style={caption(push.synced < push.apartmentCount ? 'var(--status-rec)' : 'var(--text-secondary)')}>
          Oznaczono jako wysłane: {push.synced}/{push.apartmentCount}
        </span>
      )}
      {push.status === 'done' && !push.dryRun && wynik?.statusAfter && (
        <span style={caption('var(--text-secondary)')}>Status po zmianie: {wynik.statusAfter}</span>
      )}
      {finished && (wynik?.heldLokale || []).map((h) => (
        <span key={h.nr} style={caption('var(--status-rec)')}>Lokal {h.nr} wstrzymany: {h.reason}</span>
      ))}
      {finished && diff?.summary && <span style={caption('var(--text-secondary)')}>{diff.summary}</span>}
      {finished && diff && diff.unexpected > 0 && (
        <span style={caption('var(--status-deny)', true)}>
          Nieoczekiwane różnice między kopiami: {diff.unexpected} — sprawdź raport przebiegu
        </span>
      )}
      {alarm && (
        <span
          role="alert"
          data-testid="ceeb-edit-issued-alarm"
          style={{
            ...caption('var(--status-deny)'),
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            padding: 'var(--space-1) var(--space-2)',
            borderRadius: 'var(--radius-md)',
            border: 'var(--border-width) solid var(--status-deny-bar)',
            background: 'var(--status-deny-bg)',
          }}
        >
          <span style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'flex-start', fontWeight: 'var(--weight-semibold)' }}>
            <Icon name="triangle-alert" size={14} style={{ flex: 'none', marginTop: 2 }} />
            <span>ALARM: {alarm.message || 'nieoczekiwana zmiana w CEEB — sprawdź raport przebiegu'}</span>
          </span>
          {zmiany.slice(0, ALARM_LINES).map((u, i) => (
            <span key={`${u.path}-${i}`}>{alarmChangeText(u)}</span>
          ))}
          {zmiany.length > ALARM_LINES && <span>…i {zmiany.length - ALARM_LINES} więcej — szczegóły w raporcie przebiegu</span>}
        </span>
      )}
      {push.snapshotBeforeId && (
        <SnapshotPdfButton
          buildingId={buildingId}
          snapshotId={push.snapshotBeforeId}
          label="Kopia przed zmianą (PDF)"
          fallbackName={nazwa('przed')}
        />
      )}
      {finished && push.snapshotAfterId && (
        <SnapshotPdfButton
          buildingId={buildingId}
          snapshotId={push.snapshotAfterId}
          label="Kopia po zmianie (PDF)"
          fallbackName={nazwa('po')}
        />
      )}
      {finished && push.reportUrl && (
        <a href={push.reportUrl} target="_blank" rel="noreferrer" style={caption('var(--action-primary)')}>
          Raport przebiegu
        </a>
      )}
    </>
  );
}
